import dayjs from "dayjs";

/**
 * Обчислює повні дати/часи для кожної зупинки маршруту на основі baseDate.
 * Якщо час наступної зупинки менший за попередній (наприклад, після півночі), додається 1 день.
 * Не змінює вхідні об'єкти — повертає нові з полями departure_datetime та/або arrival_datetime.
 *
 * @param {dayjs.Dayjs} baseDate - базова дата рейсу
 * @param {Array<{ departure_time?: string, arrival_time?: string, ... }>} stopsData - зупинки з полями departure_time, arrival_time ("HH:mm" або "HH:mm:ss")
 * @returns {Array} масив зупинок з доданими departure_datetime, arrival_datetime (dayjs)
 */
export const adjustTimes = (baseDate, stopsData) => {
  if (!stopsData?.length) return [];

  let previousDepartureDateTime = dayjs(baseDate).hour(0).minute(0).second(0);
  let previousArrivalDateTime = dayjs(baseDate).hour(0).minute(0).second(0);

  return stopsData.map((stop) => {
    let departureDateTime = null;
    let arrivalDateTime = null;

    if (stop.departure_time) {
      const parts = stop.departure_time.split(":").map(Number);
      const depHours = parts[0] ?? 0;
      const depMinutes = parts[1] ?? 0;
      departureDateTime = previousDepartureDateTime
        .hour(depHours)
        .minute(depMinutes)
        .second(0);

      if (departureDateTime.isBefore(previousDepartureDateTime)) {
        departureDateTime = departureDateTime.add(1, "day");
      }
      previousDepartureDateTime = departureDateTime;
    }

    if (stop.arrival_time) {
      const parts = stop.arrival_time.split(":").map(Number);
      const arrHours = parts[0] ?? 0;
      const arrMinutes = parts[1] ?? 0;
      arrivalDateTime = previousArrivalDateTime
        .hour(arrHours)
        .minute(arrMinutes)
        .second(0);

      if (arrivalDateTime.isBefore(previousArrivalDateTime)) {
        arrivalDateTime = arrivalDateTime.add(1, "day");
      }
      previousArrivalDateTime = arrivalDateTime;
    }

    return {
      ...stop,
      ...(departureDateTime !== null && { departure_datetime: departureDateTime }),
      ...(arrivalDateTime !== null && { arrival_datetime: arrivalDateTime }),
    };
  });
};

/** Час відправлення зупинки (fallback на arrival, якщо departure не задано). */
export const getStopDepartureDatetime = (stop) =>
  stop?.departure_datetime ?? stop?.arrival_datetime ?? null;

/** Час прибуття зупинки (fallback на departure, якщо arrival не задано). */
export const getStopArrivalDatetime = (stop) =>
  stop?.arrival_datetime ?? stop?.departure_datetime ?? null;

/** Тривалість сегменту між двома зупинками в хвилинах. */
export const getSegmentDurationMinutes = (fromStop, toStop) => {
  const start = getStopDepartureDatetime(fromStop);
  const end = getStopArrivalDatetime(toStop);
  if (!start || !end) return null;
  return end.diff(start, "minutes");
};
